class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', async (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      await cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
    
    this.cartUpdateUnsubscriber = undefined;
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = document.querySelector(`#Quantity-${id}`);
    if (!input) return;
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents-total',
      },
    ];
  }

  async updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    // Validate service schedule before updating quantity (skip validation when deleting/quantity = 0)
    if (quantity > 0 && !(await this.validateServiceSchedule(line, quantity, variantId))) {
      // Reset input value to previous valid value
      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      if (quantityElement) {
        quantityElement.value = quantityElement.getAttribute('value');
        quantityElement.reportValidity && quantityElement.reportValidity();
      }
      this.disableLoading(line);
      return;
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        document.dispatchEvent(new CustomEvent("zapiet:start"));
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  async validateServiceSchedule(line, quantity, variantId) {
    try {
      // Early exit if service schedule is not properly configured
      if (!window.nova || !window.nova.service_schedule) {
        throw new Error('Service schedule configuration is missing');
      }

      const schedule = window.nova.service_schedule;
      const currentDate = new Date();
      
      // Helper function to safely parse dates
      const formatScheduleDate = (dateString) => {
        if (!dateString) throw new Error('Invalid date string');
        return new Date(dateString.replace('///', ' '));
      };

      const initialDate = formatScheduleDate(schedule.initial_date);
      const finalDate = formatScheduleDate(schedule.final_date);

      // Get product-specific quantity limit from data attribute
      const quantityInput = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      const productQuantityLimit = parseInt(quantityInput && quantityInput.dataset.serviceScheduleProductQuantiteMaxParClient) || 0;

      // Check if we need to validate quantities
      const isWithinSchedulePeriod = currentDate >= initialDate && currentDate <= finalDate;
      const hasQuantityLimit = productQuantityLimit > 0;
      
      if (!isWithinSchedulePeriod) {
        throw new Error('The current date is not within the sales period');
      }

      // Get current variant ID and quantity
      if (!variantId || isNaN(quantity) || quantity < 0) {
        throw new Error('Invalid form data');
      }

      // Fetch and calculate cart quantities
      let cartData;
      const cartResponse = await fetch(`${routes.cart_url}`, { ...fetchConfig() });
      if (!cartResponse.ok) {
        throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      }
      cartData = await cartResponse.json();

      // Calculate cart quantities (excluding the current item being updated)
      const cartQuantities = this.calculateCartQuantities(cartData, variantId, line);
      
      // Calculate order quantities from the current sales period
      const orderQuantities = this.calculateOrderQuantities(
        schedule.customer_orders,
        variantId,
        initialDate,
        finalDate
      );

      // Calculate total quantities
      const totalQuantity = cartQuantities.total + quantity + orderQuantities.total;
      const totalCurrentProductQuantity = 
        cartQuantities.currentProduct + 
        quantity + 
        orderQuantities.currentProduct;

      // Validate total cases limit
      if (totalQuantity > schedule.max_item && schedule.max_item > 0) {
        const message = schedule.Strings.cases_limit_error.replace("[max_item]", schedule.max_item);
        this.showConfirmationModal(message);
        throw new Error(`Cases limit exceeded: ${totalQuantity}/${schedule.max_item}`);
      }

      // Validate product-specific limit
      if (hasQuantityLimit && totalCurrentProductQuantity > productQuantityLimit) {
        const message = schedule.Strings.max_item_error.replace("[max_item]", productQuantityLimit);
        this.showConfirmationModal(message);
        throw new Error(`Product limit exceeded: ${totalCurrentProductQuantity}/${productQuantityLimit}`);
      }

      return true;
    } catch (error) {
      console.error('Error in validateServiceSchedule:', error);
      return false;
    }
  }

  calculateCartQuantities(cartData, variantId, excludeLine = null) {
    const result = { total: 0, currentProduct: 0 };
    
    if (!cartData || !cartData.items || !Array.isArray(cartData.items)) return result;
    
    for (let i = 0; i < cartData.items.length; i++) {
      const item = cartData.items[i];
      const itemLine = i + 1;
      
      // Skip the item being updated
      if (excludeLine && itemLine === parseInt(excludeLine)) {
        continue;
      }
      
      const quantity = item.quantity || 0;
      result.total += quantity;
      
      if (String(item.variant_id) === String(variantId)) {
        result.currentProduct += quantity;
      }
    }
    
    return result;
  }

  calculateOrderQuantities(orders, variantId, initialDate, finalDate) {
    const result = { total: 0, currentProduct: 0 };
    
    if (!Array.isArray(orders)) return result;
    
    // Helper function to safely parse Shopify date format for Safari compatibility
    const parseShopifyDate = (dateString) => {
      if (!dateString) return null;
      
      // More explicit approach to handle all Shopify date formats
      let isoString = dateString;
      
      // Handle the case where there's a space before timezone with colon
      if (isoString.includes(' -') && isoString.includes(':')) {
        // "2024-08-15T11:07:09 -04:00" -> "2024-08-15T11:07:09-04:00"
        isoString = isoString.replace(' -', '-');
      }
      // Handle the case where there's a space before timezone without colon
      else if (isoString.includes(' -') && !isoString.includes(':')) {
        // "2024-08-15 11:07:09 -0400" -> "2024-08-15T11:07:09-04:00"
        isoString = isoString.replace(' ', 'T').replace(' -', '-').replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
      }
      // Handle the case where there's a space between date and time
      else if (isoString.includes(' ') && !isoString.includes('T')) {
        // "2024-08-15 11:07:09-0400" -> "2024-08-15T11:07:09-04:00"
        isoString = isoString.replace(' ', 'T').replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
      }
      
      return new Date(isoString);
    };
    
    for (const order of orders) {
      const createdAt = parseShopifyDate(order.created_at);
  
      if (order.financial_status === 'refunded' || 
          !createdAt || 
          createdAt < initialDate || 
          createdAt > finalDate) {
        continue;
      }

      for (const item of order.items || []) {
        const quantity = item.quantity || 0;
        result.total += quantity;
        
        if (item.variant_id === Number(variantId)) {
          result.currentProduct += quantity;
        }
      }
    }
    
    return result;
  }

  showConfirmationModal(message) {
    // Use the global confirmation modal function
    if (typeof window.showConfirmationModal === 'function') {
      window.showConfirmationModal(message);
    } else {
      // Fallback to alert if modal function is not available
      alert(message);
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
